/**
 * Web module discovery via `import.meta.glob` (no central registry,
 * BUILD-PLAN 5.1 rule 1). Manifests load eagerly (they are small data);
 * pages stay lazy through each module's `pages` loaders.
 */
import { assertValidManifestSet, NAV_GROUPS, type NavGroup, type NavItem } from '@velora/contracts';
import type { WebModule } from './module.js';

export type WebModuleGlob = Record<string, { default?: unknown }>;

const MODULE_PATH_RE = /\/modules\/([^/]+)\/module\.ts$/;

function isWebModule(value: unknown): value is WebModule {
  return (
    typeof value === 'object' &&
    value !== null &&
    'manifest' in value &&
    'pages' in value &&
    typeof (value as { pages: unknown }).pages === 'object'
  );
}

export function loadWebModules(glob: WebModuleGlob): WebModule[] {
  const modules: WebModule[] = [];
  for (const [path, mod] of Object.entries(glob)) {
    const folder = MODULE_PATH_RE.exec(path)?.[1];
    if (folder === undefined) throw new Error(`unexpected module path ${path}`);
    if (!isWebModule(mod.default)) {
      throw new Error(`${path} must default-export defineWebModule({ manifest, pages })`);
    }
    const { manifest, pages } = mod.default;
    if (manifest.id !== folder) {
      throw new Error(
        `${path}: manifest id "${manifest.id}" must equal its folder name "${folder}"`,
      );
    }
    for (const route of manifest.routes) {
      if (typeof pages[route.id] !== 'function') {
        throw new Error(`${path}: route "${route.id}" has no page loader`);
      }
    }
    modules.push(mod.default);
  }
  modules.sort((a, b) => a.manifest.id.localeCompare(b.manifest.id));
  assertValidManifestSet(modules.map((m) => m.manifest));
  return modules;
}

let discovered: readonly WebModule[] | undefined;

/** All modules under src/modules, validated once and cached. */
export function discoverWebModules(): readonly WebModule[] {
  discovered ??= loadWebModules(
    import.meta.glob<{ default?: unknown }>('../modules/*/module.ts', { eager: true }),
  );
  return discovered;
}

export interface ResolvedNavItem extends NavItem {
  moduleId: string;
  path: string;
}

/** Nav items grouped in BUILD-PLAN 3.4 order, sorted by `order` then label. */
export function buildNav(
  modules: readonly WebModule[],
): Array<{ group: NavGroup; items: ResolvedNavItem[] }> {
  const byGroup = new Map<NavGroup, ResolvedNavItem[]>();
  for (const { manifest } of modules) {
    for (const item of manifest.nav) {
      const route = manifest.routes.find((r) => r.id === item.routeId);
      if (!route) continue; // already rejected by assertValidManifestSet
      const list = byGroup.get(item.group) ?? [];
      // A nav item needs its own permission or, failing that, its route's:
      // the nav never offers a page the role cannot open (BUILD-PLAN 2.6).
      const permission = item.permission ?? route.permission;
      list.push({
        ...item,
        moduleId: manifest.id,
        path: route.path,
        ...(permission ? { permission } : {}),
      });
      byGroup.set(item.group, list);
    }
  }
  return NAV_GROUPS.filter((g) => byGroup.has(g)).map((group) => ({
    group,
    items: (byGroup.get(group) ?? []).sort(
      (a, b) => a.order - b.order || a.label.localeCompare(b.label),
    ),
  }));
}
